import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  LogOut, FileText, Clock, CheckCircle, XCircle,
  Inbox, Filter, Trash2, ExternalLink, RefreshCw
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo-cropped.png";

const STATUS_MAP = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-800', icon: Inbox },
  in_progress: { label: 'In Progress', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [quotesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/quotes`, { params: { status: filterStatus } }),
        axios.get(`${API}/admin/stats`),
      ]);
      setQuotes(quotesRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  }, [filterStatus, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateStatus = async (quoteId, newStatus) => {
    try {
      await axios.put(`${API}/admin/quotes/${quoteId}/status`, { status: newStatus });
      fetchData();
    } catch {}
  };

  const deleteQuote = async (quoteId) => {
    if (!window.confirm('Delete this quote request?')) return;
    try {
      await axios.delete(`${API}/admin/quotes/${quoteId}`);
      setSelectedQuote(null);
      fetchData();
    } catch {}
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const formatDate = (isoStr) => {
    try {
      return new Date(isoStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return isoStr; }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Top Bar */}
      <header className="bg-[#0f3b2e] text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Dedari" className="h-8" />
            <span className="text-sm font-semibold uppercase tracking-wider text-white/60">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/60 hidden sm:inline">{user?.email}</span>
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              className="text-white/60 hover:text-white transition-colors"
              data-testid="view-site-link"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-testid="admin-stats">
            {[
              { label: 'Total Quotes', value: stats.total_quotes, icon: FileText, color: 'text-slate-600' },
              { label: 'New', value: stats.new, icon: Inbox, color: 'text-blue-600' },
              { label: 'In Progress', value: stats.in_progress, icon: Clock, color: 'text-yellow-600' },
              { label: 'Completed', value: stats.completed, icon: CheckCircle, color: 'text-green-600' },
              { label: 'Rejected', value: stats.rejected, icon: XCircle, color: 'text-red-600' },
            ].map((stat, i) => (
              <div key={i} className="bg-white border border-slate-200 p-5" data-testid={`stat-card-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  <span className="text-2xl font-black text-slate-900">{stat.value}</span>
                </div>
                <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{stat.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filter Bar */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px] rounded-none border-slate-200" data-testid="filter-status-select">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Quotes</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <button
            onClick={fetchData}
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#0f3b2e] transition-colors"
            data-testid="refresh-button"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Quotes Table + Detail Panel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Table */}
          <div className={`${selectedQuote ? 'lg:col-span-2' : 'lg:col-span-3'} bg-white border border-slate-200 overflow-hidden`}>
            {loading ? (
              <div className="p-12 text-center text-slate-400">Loading...</div>
            ) : quotes.length === 0 ? (
              <div className="p-12 text-center" data-testid="empty-quotes-message">
                <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No quote requests found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 border-b-2 border-[#0f3b2e]">
                      <TableHead className="font-bold text-xs uppercase tracking-wider">Company</TableHead>
                      <TableHead className="font-bold text-xs uppercase tracking-wider">Contact</TableHead>
                      <TableHead className="font-bold text-xs uppercase tracking-wider">Product</TableHead>
                      <TableHead className="font-bold text-xs uppercase tracking-wider">Country</TableHead>
                      <TableHead className="font-bold text-xs uppercase tracking-wider">Status</TableHead>
                      <TableHead className="font-bold text-xs uppercase tracking-wider">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quotes.map((q, i) => {
                      const statusInfo = STATUS_MAP[q.status] || STATUS_MAP.new;
                      return (
                        <TableRow
                          key={q.id}
                          className={`cursor-pointer hover:bg-slate-50 transition-colors ${selectedQuote?.id === q.id ? 'bg-slate-50' : ''}`}
                          onClick={() => setSelectedQuote(q)}
                          data-testid={`quote-row-${i}`}
                        >
                          <TableCell className="font-medium text-slate-900">{q.company_name}</TableCell>
                          <TableCell>
                            <div className="text-sm text-slate-700">{q.full_name}</div>
                            <div className="text-xs text-slate-400">{q.email}</div>
                          </TableCell>
                          <TableCell className="text-sm">{q.product_interest}</TableCell>
                          <TableCell className="text-sm">{q.country}</TableCell>
                          <TableCell>
                            <Badge className={`${statusInfo.color} text-xs font-medium`}>
                              {statusInfo.label}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-slate-500">{formatDate(q.created_at)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          {/* Detail Panel */}
          {selectedQuote && (
            <div className="bg-white border border-slate-200 p-6" data-testid="quote-detail-panel">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-lg font-bold text-slate-900">Quote Details</h3>
                <button
                  onClick={() => setSelectedQuote(null)}
                  className="text-slate-400 hover:text-slate-600 text-sm"
                  data-testid="close-detail-button"
                >
                  Close
                </button>
              </div>

              <div className="space-y-4 text-sm">
                <div className="border-b border-slate-100 pb-3">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Company</p>
                  <p className="text-slate-900 font-medium">{selectedQuote.company_name}</p>
                </div>
                <div className="border-b border-slate-100 pb-3">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Contact Person</p>
                  <p className="text-slate-900">{selectedQuote.full_name}</p>
                </div>
                <div className="grid grid-cols-2 gap-4 border-b border-slate-100 pb-3">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Email</p>
                    <a href={`mailto:${selectedQuote.email}`} className="text-[#d05a30] hover:underline">{selectedQuote.email}</a>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">WhatsApp</p>
                    <a href={`https://wa.me/${selectedQuote.whatsapp?.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="text-[#d05a30] hover:underline">
                      {selectedQuote.whatsapp}
                    </a>
                  </div>
                </div>
                <div className="border-b border-slate-100 pb-3">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Country</p>
                  <p className="text-slate-900">{selectedQuote.country}</p>
                </div>
                <div className="grid grid-cols-2 gap-4 border-b border-slate-100 pb-3">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Product</p>
                    <p className="text-slate-900">{selectedQuote.product_interest}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Quantity</p>
                    <p className="text-slate-900">{selectedQuote.estimated_quantity}</p>
                  </div>
                </div>
                <div className="border-b border-slate-100 pb-3">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Incoterms</p>
                  <p className="text-slate-900 font-medium">{selectedQuote.incoterms}</p>
                </div>
                {selectedQuote.message && (
                  <div className="border-b border-slate-100 pb-3">
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Message</p>
                    <p className="text-slate-700">{selectedQuote.message}</p>
                  </div>
                )}
                <div className="border-b border-slate-100 pb-3">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-1">Date</p>
                  <p className="text-slate-700">{formatDate(selectedQuote.created_at)}</p>
                </div>

                {/* Status Update */}
                <div className="pt-2">
                  <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-2">Update Status</p>
                  <Select
                    value={selectedQuote.status}
                    onValueChange={(val) => updateStatus(selectedQuote.id, val)}
                  >
                    <SelectTrigger className="rounded-none border-slate-200" data-testid="update-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <a
                    href={`mailto:${selectedQuote.email}?subject=RE: Quote Request - DEDARI BALI EXPORT`}
                    className="flex-1 bg-[#0f3b2e] text-white text-center py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#164d3d] transition-colors"
                    data-testid="reply-email-button"
                  >
                    Reply via Email
                  </a>
                  <button
                    onClick={() => deleteQuote(selectedQuote.id)}
                    className="p-2.5 border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                    data-testid="delete-quote-button"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
